import React, { useState, useEffect } from "react";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import navImage from "../../assets/Logo.jpg";
import { Link } from 'react-router-dom';

const HoaNavbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (showMenu) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showMenu]);

    const handleMenuToggle = () => {
        setShowMenu(!showMenu);
    };

    const closeMenu = () => {
        setShowMenu(false);
    };

    // Navigation items
    const navItems = [
        // { to: "hoa-offer", label: "Services" },
        // { to: "hoa-plan", label: "Pricing" },
        // { to: "hoa-contact", label: "Contact" },
        { to: "/login", label: "Login", isExternal: true, isButton: true },
        // { to: "hoa-contact", label: "Book Demo", isButton: true },
    ];

    // Framer Motion variants
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, transition: { duration: 0.3 } },
    };

    const menuVariants = {
        hidden: {
            x: "100%",
            opacity: 0,
            transition: { duration: 0.3, ease: "easeInOut" }
        },
        visible: {
            x: 0,
            opacity: 1,
            transition: { duration: 0.4, ease: "easeInOut" }
        },
        exit: {
            x: "100%",
            opacity: 0,
            transition: { duration: 0.3, ease: "easeInOut" }
        },
    };

    const menuItemVariants = {
        hidden: { x: 50, opacity: 0 },
        visible: (i) => ({
            x: 0,
            opacity: 1,
            transition: {
                delay: i * 0.1,
                duration: 0.5,
                ease: "easeOut",
            },
        }),
        exit: { x: 50, opacity: 0 },
    };

    const navItemHoverVariants = {
        hover: {
            scale: 1.05,
            y: -2,
            transition: { duration: 0.2, ease: "easeOut" }
        },
        tap: { scale: 0.95 },
    };

    const logoVariants = {
        hover: { scale: 1.05, transition: { duration: 0.2 } },
    };

    // Hamburger animation variants
    const hamburgerVariants = {
        closed: { rotate: 0 },
        open: { rotate: 45 },
    };

    return (
        <motion.div
            className={`fixed z-[10000] w-full transition-all duration-500 ease-in-out ${scrolled
                    ? "bg-white/95 backdrop-blur-lg shadow-xl border-b border-gray-100"
                    : "bg-white/80 backdrop-blur-sm shadow-md"
                }`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-20">

                    {/* Logo */}
                    <motion.div
                        className="flex-shrink-0 z-[10001]"
                        variants={logoVariants}
                        whileHover="hover"
                    >
                        <Link to="/">
                            <img
                                src={navImage}
                                alt="HOA Logo"
                                className="h-20 md:h-24 w-auto sm:h-12 cursor-pointer"
                            />
                        </Link>
                    </motion.div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-1">
                        {navItems.map((item, index) => (
                            <motion.div
                                key={item.label}
                                variants={navItemHoverVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                {item.isExternal ? (
                                    <RouterLink
                                        to={item.to}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex justify-center items-center ${item.isButton
                                                ? "bg-[#252e9d] text-white hover:bg-[#1e2578] shadow-lg hover:shadow-xl"
                                                : "text-gray-700 hover:text-[#252e9d] hover:bg-gray-50"
                                            }`}
                                    >
                                        <span>{item.label}</span>
                                    </RouterLink>
                                ) : (
                                    <ScrollLink
                                        to={item.to}
                                        smooth={true}
                                        duration={500}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer ${item.isButton
                                                ? "bg-[#252e9d] text-white hover:bg-[#1e2578] shadow-lg hover:shadow-xl"
                                                : "text-gray-700 hover:text-[#252e9d] hover:bg-gray-50"
                                            }`}
                                    >
                                        {item.label}
                                    </ScrollLink>
                                )}
                            </motion.div>
                        ))}
                    </nav>

                    {/* Mobile Menu Button */}
                    <motion.button
                        className="lg:hidden z-[10000] relative w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                        onClick={handleMenuToggle}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Toggle menu"
                    >
                        <motion.div
                            className="text-gray-700 text-lg"
                            initial={false}
                            animate={showMenu ? "open" : "closed"}
                            variants={hamburgerVariants}
                        >
                            <FontAwesomeIcon icon={showMenu ? faTimes : faBars} />
                        </motion.div>
                    </motion.button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {showMenu && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] lg:hidden"
                            variants={overlayVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={closeMenu}
                        />

                        {/* Mobile Menu */}
                        <motion.div
                            className="fixed top-0 right-0 h-full min-h-screen w-full max-w-sm bg-[#252e9d] shadow-2xl z-[100000] lg:hidden"
                            variants={menuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="flex flex-col h-full">
                                {/* Menu Header */}
                                <div className="flex items-center justify-between p-0 px-4 border-b border-white/20">
                                    <img
                                        src={navImage}
                                        alt="HOA Logo"
                                        style={{filter:"brightness(100)",}}
                                        className="h-20 w-auto scale-150"
                                    />
                                    <button
                                        onClick={closeMenu}
                                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
                                        aria-label="Close menu"
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="text-white text-sm" />
                                    </button>
                                </div>

                                {/* Menu Items */}
                                <nav className="flex-1 px-6 py-8 bg-[#252e9d]">
                                    <ul className="space-y-2">
                                        {navItems.map((item, index) => (
                                            <motion.li
                                                key={item.label}
                                                custom={index}
                                                variants={menuItemVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                            >
                                                {item.isExternal ? (
                                                    <RouterLink
                                                        to={item.to}
                                                        onClick={closeMenu}
                                                        className={`block w-full text-left px-4 py-4 rounded-xl transition-all duration-300 ${item.isButton
                                                                ? "bg-white text-[#252e9d] hover:bg-gray-100 shadow-lg text-center font-semibold"
                                                                : "text-white hover:text-white hover:bg-white/10 font-medium"
                                                            }`}
                                                    >
                                                        {item.label}
                                                    </RouterLink>
                                                ) : (
                                                    <ScrollLink
                                                        to={item.to}
                                                        smooth={true}
                                                        duration={500}
                                                        onClick={closeMenu}
                                                        className={`block w-full text-left px-4 py-4 rounded-xl transition-all duration-300 cursor-pointer ${item.isButton
                                                                ? "bg-white text-[#252e9d] hover:bg-gray-100 shadow-lg text-center font-semibold"
                                                                : "text-white hover:text-white hover:bg-white/10 font-medium"
                                                            }`}
                                                    >
                                                        {item.label}
                                                    </ScrollLink>
                                                )}
                                            </motion.li>
                                        ))}
                                    </ul>
                                </nav>

                                {/* Menu Footer */}
                                {/* <div className="p-6 border-t border-white/20">
                                    <p className="text-sm text-white/70 text-center">
                                        � 2025 HOA Management
                                    </p>
                                </div> */}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default HoaNavbar;